/*

  Global Slider Component
  ------------------------

  A class representing a global slider component that uses the Keen Slider library. It allows customization through data-attributes set on the slider container. The slider is responsive and can have different number of slides displayed depending on the screen size. It also supports navigation and controls.

  Note** data-attributes should be set on slider container to control the following options:

  maxWidth <data-max-width> slider breaks after this screen width ( default none )
  minWidth <data-min-width> slider breaks before this screen width ( default 0 )
  perView desktop <data-per-view-desktop> how many slides will display on desktop ( default 1 )
  perView mobile <data-per-view-mobile> how many slides will display on mobile/tablet ( default 1 )
  hasLoop <data-slider-loop> does slider loopp (default false)


*/

import "keen-slider/keen-slider.min.css";
import KeenSlider from "keen-slider";

/** Safe value for [data-key="..."] selectors (Shopify section ids can include -- and "). */
function escapeDataKeyAttr(key) {
  if (key == null || key === "") {
    return "";
  }
  return String(key).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * Prefer controls/nav inside the same section tree as the slider to avoid selector edge cases.
 */
function querySliderPart(container, tagName, key) {
  const k = escapeDataKeyAttr(key);
  if (!k) {
    return null;
  }
  const sel = `${tagName}[data-key="${k}"]`;
  return (
    container.parentElement?.querySelector(sel) ??
    container.closest(".shopify-section")?.querySelector(sel) ??
    document.querySelector(sel)
  );
}

/**
 * Match announcement slides to a stable bar height.
 * - Injected promo mirror slides are measured against "simple" slides only (not full clone height).
 * - Slides with a countdown ([data-end-date]) are excluded from the shared max so one tall timer
 *   row does not force every slide to ~150px+; those slides keep their natural height.
 * - Keen’s `.keen-slider__slide { min-height: 100% }` makes every slide as tall as the tallest
 *   slide, so offsetHeight is wrong until we override min-height for measurement.
 * - The flex row also uses default `align-items: stretch`, so every slide’s offsetHeight matches
 *   the tallest slide (e.g. countdown row) unless we measure with `align-self: flex-start`.
 * @param {HTMLElement | null | undefined} container - keen-slider root
 */
export function equalizeKeenAnnouncementSlides(container) {
  if (!container || !container.hasAttribute("data-equal-slide-height")) {
    return;
  }
  try {
    equalizeKeenAnnouncementSlidesInner(container);
  } catch (err) {
    console.error("[equalizeKeenAnnouncementSlides]", err);
  }
}

function equalizeKeenAnnouncementSlidesInner(container) {
  const allSlides = container.querySelectorAll(".keen-slider__slide");
  if (!allSlides.length) {
    return;
  }

  const isMirrorSlide = (el) =>
    el.hasAttribute("data-injected-promotion-slide") ||
    el.classList.contains("announcement-bar__promo-mirror");

  const hasCountdown = (el) => el.querySelector("[data-end-date]");

  allSlides.forEach((el) => {
    el.style.minHeight = "";
  });

  allSlides.forEach((el) => {
    el.style.setProperty("min-height", "0", "important");
  });

  /** @type {Map<Element, string>} */
  const prevAlignSelf = new Map();
  allSlides.forEach((el) => {
    prevAlignSelf.set(el, el.style.alignSelf);
    el.style.setProperty("align-self", "flex-start", "important");
  });

  const heights = new Map();
  allSlides.forEach((el) => {
    heights.set(el, el.offsetHeight);
  });

  allSlides.forEach((el) => {
    const prev = prevAlignSelf.get(el);
    if (prev) {
      el.style.alignSelf = prev;
    } else {
      el.style.removeProperty("align-self");
    }
  });

  allSlides.forEach((el) => {
    el.style.removeProperty("min-height");
  });

  const basisSlides = [...allSlides].filter(
    (el) => !isMirrorSlide(el) && !hasCountdown(el)
  );
  const basisForMax = basisSlides.length
    ? basisSlides
    : [...allSlides].filter((el) => !isMirrorSlide(el));

  let max = 0;
  basisForMax.forEach((el) => {
    max = Math.max(max, heights.get(el) || 0);
  });

  if (max <= 0) {
    return;
  }

  allSlides.forEach((el) => {
    if (isMirrorSlide(el)) {
      el.style.minHeight = `${max}px`;
    } else if (hasCountdown(el)) {
      const h = heights.get(el) || max;
      el.style.minHeight = `${Math.max(max, h)}px`;
    } else {
      el.style.minHeight = `${max}px`;
    }
  });
}

export class Slider {
  /*
  Creates an instance of Slider.
  @constructor
  @param {HTMLElement} container - The HTML element that contains the slider.

*/
  constructor(container) {
    this.breakpoint = 1024;
    this.key = container.dataset.key;

    /**
    An object containing information about the slider.
      @property {HTMLElement} container - The HTML element that contains the slider.
      @property {NodeListOf<HTMLElement>} slides - The HTML elements representing the slides.
      @property {KeenSlider} instance - The instance of the KeenSlider library used to create the slider.
      @property {number} maxWidth - The screen width at which the slider stops rendering (default none).
      @property {number} minWidth - The screen width at which the slider starts rendering (default 0).
      @property {object} perView - An object that contains the number of slides to display depending on the screen size.
      @property {number} perView.desktop - The number of slides to display on desktop (default 1).
      @property {number} perView.mobile - The number of slides to display on mobile/tablet (default 1).
    */
    this.slider = {
      container,
      slides: container.querySelectorAll(".keen-slider__slide"),
      instance: false,
      maxWidth: false,
      minWidth: false,
      renderMode: false,
      autoRotate: false,
      perView: {
        desktop: false,
        tablet: false,
        mobile: false,
      },
    };

    /**
      An object containing information about the slider navigation.
      @property {HTMLElement} container - The HTML element that contains the slider navigation.
      @property {NodeListOf<HTMLElement>} links - The HTML elements representing the navigation dots.
      @property {string} fill - The background color of the navigation dot when active.
      @private
    */
    this.nav = {
      container: querySliderPart(container, "slider-nav", this.key),
      links: false,
      fill: false,
    };

    /**
      An object containing information about the slider controls.
      @property {HTMLElement} container - The HTML element that contains the slider controls.
      @property {HTMLElement} prev - The HTML element representing the previous control.
      @property {HTMLElement} next - The HTML element representing the next control.
    */
    this.controls = {
      container: querySliderPart(container, "slider-controls", this.key),
      prev: false,
      next: false,
    };

    /**
    The current slide index.
    @type {number}
    */
    this.index = 0;

    this._boundEqualizeHeights = this._equalizeSlideHeights.bind(this);

    this._init();
  }

  /** Current slide elements (re-query after DOM changes e.g. pruned promo slides). */
  _slideElements() {
    return this.slider.container.querySelectorAll(".keen-slider__slide");
  }

  /**
   * When data-equal-slide-height is set, set each slide min-height to the tallest
   * slide so the bar does not jump between messages.
   */
  _equalizeSlideHeights = () => {
    if (!this.slider.instance) {
      return;
    }
    equalizeKeenAnnouncementSlides(this.slider.container);
  };

  /**
    Builds the slider instance using the KeenSlider library.
  */
  _build = () => {
    /* Runtime collapse (single slide after prune) sets data-disabled — do not rebuild Keen. */
    if (this.slider.container.dataset.disabled === "true") {
      return;
    }
    // check if the slider is disabled and remove the attribute if it exists
    if (this.slider.container.hasAttribute("data-keen-slider-disabled")) {
      this.slider.container.removeAttribute("data-keen-slider-disabled");
    }

    // check if navigation is enabled and get the nav links and fill color
    if (this.nav.container != null) {
      this.nav.links = this.nav.container.querySelectorAll("nav-dot");
      this.nav.fill = this.nav.container.dataset.fill;
    }

    // check if controls are enabled and get the previous and next buttons
    if (this.controls.container != null) {
      this.controls.prev =
        this.controls.container.querySelector("control-prev");
      this.controls.next =
        this.controls.container.querySelector("control-next");
    }

    // check if the slider has a loop attribute (loop can fail internal init in edge cases)
    const hasLoopAttr = this.slider.container.hasAttribute("data-slider-loop");
    const slideCount = this._slideElements().length;
    const hasLoop = hasLoopAttr && slideCount >= 2;

    // check autoplay
    const hasAutoplay = this.slider.container.hasAttribute("data-autoplay");
    let plugins = [];

    if (hasAutoplay) {
      const autoplaySpeed =
        Number(this.slider.container.getAttribute("data-autoplay")) * 1000;
      plugins = [
        (slider) => {
          let timeout;
          let mouseOver = false;
          function clearNextTimeout() {
            clearTimeout(timeout);
          }
          function nextTimeout() {
            clearTimeout(timeout);
            if (mouseOver) return;
            timeout = setTimeout(() => {
              slider.next();
            }, autoplaySpeed);
          }
          slider.on("created", () => {
            slider.container.addEventListener("mouseover", () => {
              mouseOver = true;
              clearNextTimeout();
            });
            slider.container.addEventListener("mouseout", () => {
              mouseOver = false;
              nextTimeout();
            });
            nextTimeout();
          });
          slider.on("dragStarted", clearNextTimeout);
          slider.on("animationEnded", nextTimeout);
          slider.on("updated", nextTimeout);
        },
      ];
    }

    this.slider.renderMode = this.slider.container.hasAttribute("data-mode")
      ? this.slider.container.dataset.mode
      : "precision";

    try {
      // String selector re-queries the container on update(); a static NodeList goes stale
      // when slides are removed (e.g. hidden integrated promotion blocks), leaving empty track slots.
      this.slider.instance = new KeenSlider(
        this.slider.container,
        {
          selector: (root) =>
            Array.from(root.querySelectorAll(".keen-slider__slide")),
          slides: {
            spacing: 20, // space between slides
            perView: this.slider.perView.mobile ? this.slider.perView.mobile : 1, // number of slides to show on mobile
          },
          breakpoints: {
            "(min-width: 768px)": {
              slides: {
                perView: this.slider.perView.tablet
                  ? this.slider.perView.tablet
                  : 1,
                spacing: 20,
              },
            },
            "(min-width: 1024px)": {
              slides: {
                perView: this.slider.perView.desktop
                  ? this.slider.perView.desktop
                  : 1,
                spacing: 20,
              },
            },
          },
          loop: hasLoop,
          renderMode: this.slider.renderMode,
          created: (keen) => {
            this.slider.container._keenSliderInstance = keen;
            this.slider.slides = this._slideElements();

            // Use live slide list (prune/mirror can change DOM before/after hooks).
            this.slider.slides.forEach((el) => {
              el.style.opacity = "1";
            });

            try {
              this._buildNav();
              if (this.controls.container) {
                this._buildControls();
              }
            } catch (setupErr) {
              console.error(
                "[Slider] Navigation/controls setup failed:",
                setupErr
              );
            }

            requestAnimationFrame(() => {
              this._equalizeSlideHeights();
            });
          },
          updated: () => {
            this.slider.slides = this._slideElements();
            /* Liquid ships slides with opacity:0; created() fixes once. After prune/mirror,
             * promotionBanner calls update() — without this, slides stay invisible. Custom
             * renderMode still gets correct values from detailsChanged below. */
            this.slider.slides.forEach((el) => {
              el.style.opacity = "1";
            });
            requestAnimationFrame(() => {
              this._equalizeSlideHeights();
            });
          },
          slideChanged: (data) => {
            // update the slider based on the current slide index
            this._updateSlider(data.track.details.rel);
          },
          detailsChanged: (s) => {
            if (this.slider.renderMode === "custom") {
              s.slides.forEach((element, idx) => {
                element.style.opacity = s.track.details.slides[idx].portion;
              });
            }
          },
          destroyed: () => {
            delete this.slider.container._keenSliderInstance;
            this.slider.instance = false;
            if (this.slider.container.hasAttribute("data-equal-slide-height")) {
              window.removeEventListener("resize", this._boundEqualizeHeights);
            }
            // set the disabled attribute to the slider container and reset the slides
            this.slider.container.setAttribute("data-keen-slider-disabled", null);
            this._resetSlides();
          },
        },
        plugins
      );
    } catch (err) {
      console.error("[Slider] KeenSlider failed to initialize:", err);
      this.slider.instance = false;
      this.slider.container
        .querySelectorAll(".keen-slider__slide")
        .forEach((el) => {
          el.style.opacity = "1";
        });
    }

    /* Keen wraps init in try/catch and returns undefined on failure without rethrowing,
     * so created() never runs and slides stay at Liquid opacity:0. Recover explicitly. */
    if (!this.slider.container._keenSliderInstance) {
      console.error(
        "[Slider] KeenSlider did not finish initializing; see keen-slider logs above. Showing slides without carousel."
      );
      this.slider.instance = false;
      this.slider.container
        .querySelectorAll(".keen-slider__slide")
        .forEach((el) => {
          el.style.opacity = "1";
        });
    }
  };

  /**
    Updates the slider by changing the currently displayed slide and updating the navigation dots, if present.
    @param {number} newIdx - The index of the slide to display.
    @returns {void}
  */
  _updateSlider = (newIdx) => {
    // this.slider.slides[this.index].setAttribute("aria-hidden", true);
    // this.slider.slides[newIdx].setAttribute("aria-hidden", false);

    if (this.slider.renderMode === "custom") {
      const slides = this._slideElements();
      slides[this.index].style.zIndex = "1";
      slides[newIdx].style.zIndex = "5";
    }

    if (this.nav.container != null) {
      this.nav.links[this.index].style.background = "transparent";
      this.nav.links[this.index].setAttribute("aria-current", false);

      this.nav.links[newIdx].style.background = `${this.nav.fill}`;
      this.nav.links[newIdx].setAttribute("aria-current", true);
    }

    this.index = newIdx;
  };

  /**
    Handles the click event on navigation dots or controls to move the slider to the corresponding slide.
    @param {Object} e - The event object representing the click event.
  */
  _handleClick = (e) => {
    const index = e.currentTarget.dataset.key;
    this.slider.instance.moveToIdx(parseInt(index));
  };

  /**
    Handles the previous slide event
  */
  _handlePrev = () => {
    this.slider.instance.prev();
  };

  /**
    Handles the next slide event
  */
  _handleNext = () => {
    this.slider.instance.next();
  };

  /**
    Handles the keydown event
    @param {Event} e - the event object
  */
  _handleKeyDown = (e) => {
    const action = e.target.dataset.action;

    if (e.key === "Enter" && action === "prev") {
      return this._handlePrev();
    }
    if (e.key === "Enter" && action === "next") {
      return this._handleNext();
    }
    if (e.key === "ArrowLeft" && e.target.localName === "control-next") {
      return this.controls.prev.focus();
    }
    if (e.key === "ArrowRight" && e.target.localName === "control-prev") {
      return this.controls.next.focus();
    }
  };

  /**
    Builds navigation dots and adds event listener for click on each dot
  */
  _buildNav = () => {
    if (this.nav.container == null) {
      return;
    }
    this.nav.links[this.index].style.background = `${this.nav.fill}`;
    this.nav.links.forEach((el) => {
      el.addEventListener("click", this._handleClick);
    });
  };

  /**

  Sets up the slider controls by adding event listeners to the previous and next buttons.
  If the controls container is not present, returns from the function without performing any action.
  */
  _buildControls = () => {
    if (this.controls.container == null) {
      return;
    }
    this.controls.prev.addEventListener("click", this._handlePrev);
    this.controls.prev.addEventListener("keydown", this._handleKeyDown);

    this.controls.next.addEventListener("click", this._handleNext);
    this.controls.next.addEventListener("keydown", this._handleKeyDown);
  };

  /**
    Resets the slides by removing any transform applied to them and resetting aria-hidden attribute to false.
  */
  _resetSlides = () => {
    this._slideElements().forEach((el) => {
      el.style.transform = "0";
      el.removeAttribute("aria-hidden");
      el.setAttribute("aria-hidden", false);
    });
  };

  /**
    Checks the window size against the defined minimum and maximum widths for the slider.
    If the window size meets the criteria, the slider is built.
    If not, the slider is not built.
  */
  _checkSize = () => {
    if (this.slider.container.dataset.disabled === "true") {
      return;
    }
    const minWidth = this.slider.minWidth;
    const maxWidth = this.slider.maxWidth;
    const windowWidth = window.innerWidth;

    if (!minWidth && !maxWidth) {
      this._build();
      return;
    }

    // If there is a minimum width and the window is wider than the minimum, rebuild the slider
    if (minWidth && windowWidth > minWidth) {
      this._build();
      return;
    }

    // If there is a maximum width and the window is narrower than the maximum, rebuild the slider
    if (maxWidth && windowWidth < maxWidth) {
      this._build();
    }
  };

  /**
    Initializes the slider based on the provided configuration in the slider object.

    Checks the screen size and builds the slider accordingly.

    Listens for window resize events and destroys the slider instance if necessary.
  */
  _init = () => {
    if (
      this.slider.container.dataset.disabled === "true" ||
      this._slideElements().length === 0
    ) {
      return;
    }

    // Set number of slides to show on mobile and desktop devices if specified in slider container attributes
    if (this.slider.container.hasAttribute("data-per-view-mobile")) {
      this.slider.perView.mobile = parseInt(
        this.slider.container.dataset.perViewMobile
      );
    }

    if (this.slider.container.hasAttribute("data-per-view-tablet")) {
      this.slider.perView.tablet = parseInt(
        this.slider.container.dataset.perViewTablet
      );
    }

    if (this.slider.container.hasAttribute("data-per-view-desktop")) {
      this.slider.perView.desktop = parseInt(
        this.slider.container.dataset.perViewDesktop
      );
    }

    // Set maximum and minimum screen width for the slider if specified in slider container attributes
    if (this.slider.container.hasAttribute("data-max-screen-width")) {
      this.slider.maxWidth = parseInt(
        this.slider.container.dataset.maxScreenWidth
      );
    }
    if (this.slider.container.hasAttribute("data-min-screen-width")) {
      this.slider.minWidth = parseInt(
        this.slider.container.dataset.minScreenWidth
      );
    }

    // Call the _checkSize function to set the number of slides to show based on current screen width
    this._checkSize();

    if (this.slider.container.hasAttribute("data-equal-slide-height")) {
      window.addEventListener("resize", this._boundEqualizeHeights);
    }

    window.addEventListener("resize", () => {
      // If slider instance doesn't exist, call _checkSize function and return
      if (this.slider.instance === false) {
        this._checkSize();
        return;
      }

      // If window width is less than minimum width, destroy the slider instance
      if (
        this.slider.minWidth !== false &&
        window.innerWidth < this.slider.minWidth
      ) {
        this.slider.instance.destroy();
      }

      // If window width is greater than maximum width, destroy the slider instance
      if (
        this.slider.maxWidth !== false &&
        window.innerWidth > this.slider.maxWidth
      ) {
        this.slider.instance.destroy();
      }
    });
  };
}

/**
 * When Liquid rendered a carousel (2+ blocks) but prune/mirror leaves exactly one slide,
 * destroy Keen and present a static bar (matches single-block Liquid output).
 */
export function collapseAnnouncementSliderIfSingleSlide(container) {
  if (!container || !container.classList.contains("announcements-bar")) {
    return;
  }
  const slides = container.querySelectorAll(".keen-slider__slide");
  if (slides.length !== 1) {
    return;
  }

  const slide = slides[0];
  const keen = container._keenSliderInstance;
  if (keen && typeof keen.destroy === "function") {
    try {
      keen.destroy();
    } catch (err) {
      console.error("[collapseAnnouncementSliderIfSingleSlide] destroy failed:", err);
    }
  }
  if (container._keenSliderInstance) {
    delete container._keenSliderInstance;
  }

  container.setAttribute("data-disabled", "true");
  container.setAttribute("data-single-slide", "true");
  container.classList.remove("keen-slider");
  container.removeAttribute("data-slider-loop");
  container.removeAttribute("data-autoplay");
  container.removeAttribute("data-equal-slide-height");

  slide.classList.remove("keen-slider__slide");
  slide.classList.add("announcement-bar__single");
  slide.style.opacity = "1";
  slide.style.transform = "";
  slide.style.minWidth = "";
  slide.style.maxWidth = "";
  slide.style.removeProperty("min-height");

  const bar = container.closest("announcement-bar");
  bar?.querySelector("slider-controls")?.classList.add("tw-hidden");
}
